import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { useConversationsData, usePatientConversationsData } from './useConversationsData'
import { useProviderPairConversations } from './useProviderPairConversations'
import { useProviderPairMessagingAccess } from './useProviderPairMessagingAccess'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { getResolvedConversations, getSelectedConversationId } from '../messagesSelectors'
import { buildProviderPairMessengerConversation } from '../providerPairUtils'
import { MessengerConversation } from '../types'

export const useSelectedConversation = (): MessengerConversation | null => {
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const { data: conversations } = useConversationsData()
  const userId = useSelector(getAuthUserId)

  const selectedClientId = useSelector(getSelectedClientId)
  const { currentData: patientConversations } = usePatientConversationsData(
    selectedClientId
      ? {
          providerId: userId,
          patientId: selectedClientId,
          types: CONVERSATION_TYPE.PROVIDER,
        }
      : skipToken,
  )
  const { currentData: aiCoachConversations } = usePatientConversationsData(
    selectedClientId
      ? {
          providerId: userId,
          patientId: selectedClientId,
          types: CONVERSATION_TYPE.AI_COACH,
        }
      : skipToken,
  )
  const selectedConversationId = useSelector(getSelectedConversationId)
  const resolvedConversations = useSelector(getResolvedConversations)
  const { currentData: providerPairConversations } = useProviderPairConversations()
  // The list owns pair identity, read access, and capabilities. Until it includes a newly
  // ensured pair, its local composer sends with the ID returned by ensure.
  const selectedProviderPairConversation = canAccessProviderPairs
    ? providerPairConversations?.find(
        (conversation) =>
          conversation.external_conversation_id === selectedConversationId &&
          conversation.patient_lyra_id === selectedClientId &&
          conversation.current_user.participant_lyra_id === userId &&
          conversation.capabilities.can_read,
      )
    : undefined
  const selectedProviderPairMessengerConversation = useMemo(
    () =>
      selectedProviderPairConversation
        ? buildProviderPairMessengerConversation(selectedProviderPairConversation)
        : null,
    [selectedProviderPairConversation],
  )

  return useMemo(
    () =>
      conversations?.find((conversation) => conversation.conversation_id === selectedConversationId) ??
      (Array.isArray(patientConversations)
        ? patientConversations.find((conversation) => conversation.conversation_id === selectedConversationId)
        : null) ??
      (Array.isArray(aiCoachConversations)
        ? aiCoachConversations.find((conversation) => conversation.conversation_id === selectedConversationId)
        : null) ??
      resolvedConversations.find((conversation) => conversation.conversation_id === selectedConversationId) ??
      selectedProviderPairMessengerConversation ??
      null,
    [
      conversations,
      patientConversations,
      aiCoachConversations,
      resolvedConversations,
      selectedConversationId,
      selectedProviderPairMessengerConversation,
    ],
  )
}
